import React from "react";
import { Button, Modal } from "semantic-ui-react";
import { useNotifySubscriberContactMethodOptOut } from "../../../../hooks/useNotifySubscriberContactMethodOptOut";
import { buildIdentifierString } from "../utils/buildIdentifierString";
import { useNotifySubscriberContactMethod } from "../../../../hooks/useNotifySubscriberContactMethod";
import { useNotifySubscriberContactMethodDisconnect } from "../../../../hooks/useNotifySubscriberContactMethodDisconnect";
import { useQueryCache } from "react-query";
import { buildNotifyContactQueryKey } from "../../../../hooks/useNotifyContact";
import { useToasts } from "react-toast-notifications";
import { buildNotifyContactsQueryKey } from "../../../../hooks/useInfiniteNotifyContacts";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "../../../../hooks/useInfiniteNotifySubscriberContactMethods";

export function OptOutAndDisconnectSubscriberContactMethodModal({
  subscriberContactMethodId,
  notifyContactId,
  isOpen,
  close,
}: {
  subscriberContactMethodId?: number;
  notifyContactId?: number;
  isOpen: boolean;
  close: () => void;
}) {
  const { subscriberContactMethod, isSubscriberContactMethodLoading } = useNotifySubscriberContactMethod({
    id: subscriberContactMethodId,
  });

  const queryCache = useQueryCache();
  const { addToast } = useToasts();

  const { unlinkSubscriberContactMethod, isUnlinkNotifySubscriberContactMethodToPersonMutation } =
    useNotifySubscriberContactMethodDisconnect();
  const { optOutSubscriberContactMethod, isOptOutSubscriberContactMethodLoading } =
    useNotifySubscriberContactMethodOptOut({
      async onSuccess() {
        await queryCache.invalidateQueries(buildNotifyContactQueryKey({ id: notifyContactId }));
        await queryCache.invalidateQueries(buildNotifyContactsQueryKey());
        await queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
        close();
      },
    });

  const optOutAndDisconnect = async ({ subscriberContactMethodId }: { subscriberContactMethodId: number }) => {
    try {
      //Unlink the subscriber contact method
      await unlinkSubscriberContactMethod(subscriberContactMethodId);

      // Opt-out the subscriber contact method
      await optOutSubscriberContactMethod({ subscriberContactMethodIds: [subscriberContactMethodId] });

      addToast("Successfully removed and opted-out the subscriber contact method.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to remove and opt-out the subscriber contact method.", {
        appearance: "error",
        autoDismiss: true,
      });
      throw error;
    }
  };

  return (
    <Modal open={isOpen} onClose={close} closeIcon size='tiny'>
      <Modal.Header>Remove {buildIdentifierString(subscriberContactMethod)}?</Modal.Header>
      <Modal.Content>
        The user will be opted-out and no longer receive notifications at this contact method. They can re-add this
        contact method at any time.
      </Modal.Content>
      <Modal.Actions>
        <Button content='Cancel' secondary onClick={close} />
        <Button
          content='Remove'
          negative
          disabled={
            isUnlinkNotifySubscriberContactMethodToPersonMutation ||
            isOptOutSubscriberContactMethodLoading ||
            isSubscriberContactMethodLoading
          }
          loading={
            isUnlinkNotifySubscriberContactMethodToPersonMutation ||
            isOptOutSubscriberContactMethodLoading ||
            isSubscriberContactMethodLoading
          }
          onClick={async () => {
            if (!subscriberContactMethodId) return;
            await optOutAndDisconnect({ subscriberContactMethodId });
          }}
        />
      </Modal.Actions>
    </Modal>
  );
}
